import { API_BASE } from "../utils";
import {
  ApiError,
  apiFetch,
  requestJson,
  SESSION_ERROR_EVENT,
} from "./request";

/** @typedef {{id:string, album_id:number, status:"queued"|"rendering"|"ready"|"failed", label_count:number|null, warnings:string[], error:string|null, expires_at:string}} LabelJobDto */
const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isLabelJobId(value) {
  return typeof value === "string" && uuidPattern.test(value);
}

function parseJob({ data, status, requestId }, expectedStatus) {
  if (
    status !== expectedStatus ||
    !isLabelJobId(data?.id) ||
    !Number.isInteger(data?.album_id) ||
    !["queued", "rendering", "ready", "failed"].includes(data?.status) ||
    !(
      data.label_count === null ||
      (Number.isInteger(data.label_count) && data.label_count > 0)
    ) ||
    (data.status === "ready" && data.label_count === null) ||
    !Array.isArray(data.warnings) ||
    !data.warnings.every((warning) => typeof warning === "string") ||
    !(data.error === null || typeof data.error === "string") ||
    (data.status === "failed" && !data.error) ||
    typeof data.expires_at !== "string" ||
    !Number.isFinite(Date.parse(data.expires_at))
  )
    throw new ApiError("Invalid label job response.", status, requestId);
  // The UI uses an app model, separate from the wire DTO.
  return {
    id: data.id,
    albumId: data.album_id,
    status: data.status,
    labelCount: data.label_count,
    warnings: data.warnings,
    error: data.error,
    expiresAt: data.expires_at,
  };
}

export async function createLabelJob(albumId, requestId, signal) {
  const job = parseJob(
    await requestJson(`${API_BASE}/albums/${albumId}/label-print-jobs/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ request_id: requestId }),
      signal,
    }),
    202,
  );
  if (job.albumId !== Number(albumId))
    throw new Error("Label job belongs to a different release.");
  return job;
}

export async function getLabelJob(jobId, signal) {
  if (!isLabelJobId(jobId)) throw new Error("Invalid label job identifier.");
  return parseJob(
    await requestJson(`${API_BASE}/label-print-jobs/${jobId}/`, { signal }),
    200,
  );
}

export async function getLabelDocument(jobId, signal) {
  if (!isLabelJobId(jobId)) throw new Error("Invalid label job identifier.");
  const response = await apiFetch(
    `${API_BASE}/label-print-jobs/${jobId}/document/`,
    { signal },
  );
  if (!response.ok) {
    let data;
    try {
      data = await response.json();
    } catch {
      /* Middleware can return HTML. */
    }
    const error = new ApiError(
      typeof data?.error === "string"
        ? data.error
        : "Unable to retrieve label PDF.",
      response.status,
      response.headers?.get("X-Request-ID"),
    );
    if ([401, 403].includes(response.status))
      window.dispatchEvent(
        new CustomEvent(SESSION_ERROR_EVENT, { detail: error }),
      );
    throw error;
  }
  if (!response.headers?.get("Content-Type")?.startsWith("application/pdf"))
    throw new Error("Invalid label PDF response.");
  return response.blob();
}
